import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import CustomCalendar from './CustomCalendar';
import GuruCard from './utils/GuruCard';
import ConsultationCard from './utils/ConsultationCard';
import TimeSlotButton from './utils/TimeSlotButton';
import BookingItem from './utils/BookingItem';
import ValidationErrorPopup from './utils/CommonPopup';

import {
  getAllGurus,
  getAllConsultationTypes,
  getGuruAvailability,
  createBooking,
  getUserBookings,
  cancelBooking,
  sendEmailNotification,
} from '../api';

/* ----------------------
   CONSTANTS (TOP-LEVEL)
   ---------------------- */
const GURU_AVATARS = [
  '👨‍🏫', '👨‍🎓', '👳‍♂️', '🧙', '👴', '🧑‍🏫', '👨‍⚕️',
  '👨‍🔬', '🧑‍💼', '👨‍🚀', '🧝‍♂️', '🧑‍🎨', '👨‍🍳'
];

const MAX_CUSTOM_DAYS = 60;
const SCROLL_MARGIN_TOP = 20;

/* ----------------------
   HELPER UTILITIES
   ---------------------- */
const formatTime = (hour, minute = 0) => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  const displayMinute = minute === 0 ? '00' : String(minute).padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
};

const getDateLimits = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + MAX_CUSTOM_DAYS);
  maxDate.setHours(23, 59, 59, 999);

  return { min: today, max: maxDate };
};

const getWeekDates = () => {
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    dates.push({
      date,
      dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNumber: date.getDate(),
      monthName: date.toLocaleDateString('en-US', { month: 'short' }),
      isToday: i === 0,
      sqlDate: date.toISOString().split('T')[0]
    });
  }
  return dates;
};

/* ----------------------
   MAIN COMPONENT
   ---------------------- */
function CallGuru() {
  const { user, refreshCredits } = useAuth();
  const navigate = useNavigate();

  // state
  const [gurus, setGurus] = useState([]);
  const [consultationTypes, setConsultationTypes] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);

  const [selectedGuru, setSelectedGuru] = useState(null);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  // week selection default 0 (today) OR null when using customSelectedDate
  const [selectedDate, setSelectedDate] = useState(0);
  const [customSelectedDate, setCustomSelectedDate] = useState(null);

  const [showCalendar, setShowCalendar] = useState(false);

  // ✅ VALIDATION ERROR POPUP STATE
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationPopup, setShowValidationPopup] = useState(false);

  const [loading, setLoading] = useState({
    gurus: false,
    consultations: false,
    slots: false,
    bookings: false,
    booking: false
  });

  // refs for scroll/autoscroll
  const scrollRef = useRef(null);
  const guruSectionRef = useRef(null);
  const consultationSectionRef = useRef(null);
  const dateSectionRef = useRef(null);
  const timeSectionRef = useRef(null);
  const bookingSectionRef = useRef(null);

  // ✅ VALIDATION ERROR REFS
  const guruErrorRef = useRef(null);
  const consultationErrorRef = useRef(null);
  const dateErrorRef = useRef(null);
  const timeErrorRef = useRef(null);

  // persistent avatar mapping for deterministic fallback
  const [guruAvatars, setGuruAvatars] = useState({});
  const getGuruAvatar = (guruId) => {
    if (guruAvatars[guruId]) return guruAvatars[guruId];
    const avatar = GURU_AVATARS[guruId % GURU_AVATARS.length] || GURU_AVATARS[Math.floor(Math.random() * GURU_AVATARS.length)];
    setGuruAvatars(prev => ({ ...prev, [guruId]: avatar }));
    return avatar;
  };

  const weekDates = useMemo(() => getWeekDates(), []);

  /* ---------------
     API CALLS
     --------------- */
  const fetchGurus = async () => {
    setLoading(prev => ({ ...prev, gurus: true }));
    try {
      const res = await getAllGurus();
      if (res?.data?.success) setGurus(res.data.data || []);
      else console.error('fetchGurus error:', res?.data?.message);
    } catch (e) {
      console.error('fetchGurus failed:', e);
    } finally {
      setLoading(prev => ({ ...prev, gurus: false }));
    }
  };

  const fetchConsultationTypes = async () => {
    setLoading(prev => ({ ...prev, consultations: true }));
    try {
      const res = await getAllConsultationTypes();
      if (res?.data?.success) setConsultationTypes(res.data.data || []);
      else console.error('fetchConsultationTypes error:', res?.data?.message);
    } catch (e) {
      console.error('fetchConsultationTypes failed:', e);
    } finally {
      setLoading(prev => ({ ...prev, consultations: false }));
    }
  };

  const fetchAvailableSlots = async (guruId, date) => {
    if (!guruId || !date) {
      setAvailableSlots([]);
      return;
    }
    setLoading(prev => ({ ...prev, slots: true }));
    try {
      const res = await getGuruAvailability(guruId, date, date);
      if (res?.data?.success) setAvailableSlots(res.data.data || []);
      else {
        console.error('fetchAvailableSlots error:', res?.data?.message);
        setAvailableSlots([]);
      }
    } catch (e) {
      console.error('fetchAvailableSlots failed:', e);
      setAvailableSlots([]);
    } finally {
      setLoading(prev => ({ ...prev, slots: false }));
    }
  };

  const fetchUserBookings = async () => {
    if (!user?.id) return;
    setLoading(prev => ({ ...prev, bookings: true }));
    try {
      const res = await getUserBookings(user.id);
      if (res?.data?.success) setExistingBookings(res.data.data || []);
      else console.error('fetchUserBookings error:', res?.data?.message);
    } catch (e) {
      console.error('fetchUserBookings failed:', e);
    } finally {
      setLoading(prev => ({ ...prev, bookings: false }));
    }
  };

  useEffect(() => { fetchGurus(); fetchConsultationTypes(); }, []);
  useEffect(() => { if (user?.id) fetchUserBookings(); }, [user?.id]);

  // fetch slots when week selection changes
  useEffect(() => {
    if (selectedGuru && selectedDate !== null && selectedDate !== undefined) {
      const dateObj = weekDates[selectedDate];
      if (dateObj?.sqlDate) fetchAvailableSlots(selectedGuru, dateObj.sqlDate);
    }
  }, [selectedGuru, selectedDate, weekDates]);

  // fetch slots when custom date is selected
  useEffect(() => {
    if (selectedGuru && customSelectedDate) {
      const dateStr = customSelectedDate.toISOString().split('T')[0];
      fetchAvailableSlots(selectedGuru, dateStr);
    }
  }, [selectedGuru, customSelectedDate]);

  /* ---------------
     UI helpers
     --------------- */
  const generateAvailableSlots = () => {
    return availableSlots.map(slot => {
      const startTime = formatTime(slot.start_hour, slot.start_minute);
      const totalMinutes = slot.start_minute + slot.duration_minutes;
      const endHour = slot.start_hour + Math.floor(totalMinutes / 60);
      const endMinute = totalMinutes % 60;
      const endTime = formatTime(endHour, endMinute);

      return {
        id: `${slot.start_hour}-${slot.start_minute}`,
        start_hour: slot.start_hour,
        start_minute: slot.start_minute,
        duration: slot.duration_minutes,
        label: `${startTime} - ${endTime}`,
        available: true,
        type: slot.duration_minutes === 60 ? 'full' : 'half'
      };
    });
  };

  const scrollLeft = () => scrollRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
  const scrollRight = () => scrollRef.current?.scrollBy({ left: 300, behavior: 'smooth' });

  const scrollToSection = (ref) => {
    if (!ref?.current) return;

    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };

  const getDisplayDate = () => {
    if (customSelectedDate) {
      return {
        dayName: customSelectedDate.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNumber: customSelectedDate.getDate(),
        monthName: customSelectedDate.toLocaleDateString('en-US', { month: 'short' }),
        fullDate: customSelectedDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        }),
        sqlDate: customSelectedDate.toISOString().split('T')[0]
      };
    }
    return weekDates[selectedDate] || { dayName: '', monthName: '', dayNumber: '', fullDate: '' };
  };

  /* ---------------
     ✅ VALIDATION FUNCTION
     --------------- */
  const validateBooking = () => {
    const errors = [];

    // Guru validation
    if (!selectedGuru) {
      errors.push({
        field: 'guru',
        message: 'Please select a Guru',
        fieldRef: guruSectionRef
      });
    }

    // Consultation type validation
    if (!selectedConsultation) {
      errors.push({
        field: 'consultation',
        message: 'Please select a consultation type',
        fieldRef: consultationSectionRef
      });
    }

    // Date validation
    if (selectedDate === null && !customSelectedDate) {
      errors.push({
        field: 'date',
        message: 'Please select a date',
        fieldRef: dateSectionRef
      });
    }

    // Time slot validation
    if (!selectedTimeSlot) {
      errors.push({
        field: 'time',
        message: 'Please select a time slot',
        fieldRef: timeSectionRef
      });
    }

    // User login validation
    if (!user) {
      errors.push({
        field: 'login',
        message: 'Please login to continue',
        fieldRef: null
      });
    }

    // Credits validation
    if (user && selectedConsultation) {
      const consultationType = consultationTypes.find(c => c.id === selectedConsultation);
      if (consultationType && user.credits < consultationType.credits_required) {
        errors.push({
          field: 'credits',
          message: `💳 Insufficient Credits: need ${consultationType.credits_required}, have ${user.credits}`,
          fieldRef: bookingSectionRef
        });
      }
    }

    return errors;
  };

  /* ---------------
     Booking & Cancellation
     --------------- */
  const bookCall = async () => {
    // ✅ VALIDATE BEFORE BOOKING
    const errors = validateBooking();

    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowValidationPopup(true);
      return;
    }

    // ✅ IF VALIDATION PASSES, GET DATA
    const selectedSlot = generateAvailableSlots().find(s => s.id === selectedTimeSlot);
    const selectedDateObj = customSelectedDate
      ? {
        sqlDate: customSelectedDate.toISOString().split('T')[0],
        dayName: customSelectedDate.toLocaleDateString('en-US', { weekday: 'short' }),
        monthName: customSelectedDate.toLocaleDateString('en-US', { month: 'short' }),
        dayNumber: customSelectedDate.getDate()
      }
      : weekDates[selectedDate];

    if (!selectedDateObj?.sqlDate || !selectedSlot) {
      setValidationErrors([{ field: 'system', message: '⚠️ Invalid selection. Please try again.', fieldRef: null }]);
      setShowValidationPopup(true);
      return;
    }

    const consultationType = consultationTypes.find(c => c.id === selectedConsultation);
    const guruInfo = gurus.find(g => g.id === selectedGuru);

    if (!consultationType || !guruInfo) {
      setValidationErrors([{ field: 'system', message: '⚠️ Invalid selection. Please try again.', fieldRef: null }]);
      setShowValidationPopup(true);
      return;
    }

    // Duration check
    if (consultationType.duration_minutes > selectedSlot.duration) {
      setValidationErrors([{
        field: 'duration',
        message: '⏱️ Selected consultation duration does not fit in the selected time slot',
        fieldRef: timeSectionRef
      }]);
      setShowValidationPopup(true);
      return;
    }

    setLoading(prev => ({ ...prev, booking: true }));
    try {
      const bookingData = {
        userId: user.id,
        guruId: selectedGuru,
        consultationTypeId: selectedConsultation,
        bookingDate: selectedDateObj.sqlDate,
        startHour: selectedSlot.start_hour,
        startMinute: selectedSlot.start_minute,
        notes: `Booked via web app - ${consultationType.name}`
      };

      const res = await createBooking(bookingData);
      if (!res?.data?.success) {
        setValidationErrors([{
          field: 'booking',
          message: ` Booking Error: ${res?.data?.message || 'Unknown error'}`,
          fieldRef: bookingSectionRef
        }]);
        setShowValidationPopup(true);
        return;
      }

      // try to send emails but don't fail booking if emails fail
      try {
        const formattedDate = new Date(selectedDateObj.sqlDate).toLocaleDateString('en-IN', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
        });

        const emailData = {
          customerName: user.full_name || user.name,
          customerEmail: user.email,
          customerPhone: user.mobile_number || 'Not provided',
          guruName: guruInfo.name,
          consultationType: consultationType.name,
          bookingDate: formattedDate,
          startTime: selectedSlot.label,
          duration: consultationType.duration_minutes,
          creditsUsed: consultationType.credits_required,
          bookingId: res.data.booking?.id || 'N/A'
        };

        await sendEmailNotification('BOOKING_CONFIRMATION', user.email, emailData);
        await sendEmailNotification('BOOKING_BUSINESS_ALERT', null, emailData);
      } catch (emailErr) {
        console.warn('Email send failed (non-fatal):', emailErr);
      }

      // successful booking
      await refreshCredits();
      setSelectedGuru(null);
      setSelectedConsultation(null);
      setSelectedTimeSlot(null);
      setCustomSelectedDate(null);
      setSelectedDate(0);
      setAvailableSlots([]);
      fetchUserBookings();

      alert('✅ ' + (res.data.message || 'Booking successful!'));
    } catch (err) {
      console.error('Booking error:', err);

      let errorMessage = ' Error creating booking';
      if (err?.response?.status === 402) {
        errorMessage = `💳 Insufficient Credits: ${err.response?.data?.message || ''}`;
      } else if (err.message) {
        errorMessage = ` ${err.message}`;
      }

      setValidationErrors([{
        field: 'booking_error',
        message: errorMessage,
        fieldRef: bookingSectionRef
      }]);
      setShowValidationPopup(true);
    } finally {
      setLoading(prev => ({ ...prev, booking: false }));
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!bookingId || !user?.id) {
      setValidationErrors([{ field: 'cancel', message: '⚠️ Unable to cancel booking.', fieldRef: null }]);
      setShowValidationPopup(true);
      return;
    }

    setLoading(prev => ({ ...prev, booking: true }));
    try {
      const res = await cancelBooking(bookingId, user.id, 'Cancelled by user from web app');
      if (!res?.data?.success) {
        setValidationErrors([{
          field: 'cancel',
          message: ` Error: ${res?.data?.message || 'Unknown'}`,
          fieldRef: null
        }]);
        setShowValidationPopup(true);
        return;
      }

      // try send cancellation emails but don't fail operation
      try {
        const cancelledBooking = res.data.booking;
        const fullBookingData = existingBookings.find(b => b.id === bookingId) || {};

        const bookingDate = fullBookingData?.booking_date || cancelledBooking?.bookingDate;
        const formattedDate = bookingDate ? new Date(bookingDate).toLocaleDateString('en-IN', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
        }) : 'Date';

        const cancelEmailData = {
          customerName: user.full_name || user.name,
          customerEmail: user.email,
          guruName: fullBookingData?.guru_name || cancelledBooking?.guruName || 'Guru',
          consultationType: fullBookingData?.consultation_name || cancelledBooking?.consultationName || 'Consultation',
          bookingDate: formattedDate,
          startTime: fullBookingData ? formatTime(fullBookingData.start_hour, fullBookingData.start_minute) : 'Time',
          creditsRefunded: res.data.booking?.creditsRefunded || 0,
          bookingId,
          cancellationReason: 'Cancelled by user from web app'
        };

        await sendEmailNotification('CANCELLATION_CONFIRMATION', user.email, cancelEmailData);
        await sendEmailNotification('CANCELLATION_BUSINESS_ALERT', null, cancelEmailData);
      } catch (emailErr) {
        console.warn('Cancellation email failed (non-fatal):', emailErr);
      }

      await refreshCredits();
      fetchUserBookings();

      // refresh slots if needed
      if (selectedGuru && selectedDate !== null) {
        const dateObj = weekDates[selectedDate];
        if (dateObj?.sqlDate) fetchAvailableSlots(selectedGuru, dateObj.sqlDate);
      }

      alert('✅ ' + (res.data.message || 'Booking cancelled'));
    } catch (err) {
      console.error('Cancel booking error:', err);
      setValidationErrors([{
        field: 'cancel_error',
        message: ` Error cancelling booking: ${err?.message || 'Unknown'}`,
        fieldRef: null
      }]);
      setShowValidationPopup(true);
    } finally {
      setLoading(prev => ({ ...prev, booking: false }));
    }
  };

  /* ----------------------
     RENDER
     ---------------------- */
  const displayDate = getDisplayDate();

  return (
    <>
      {/* ✅ VALIDATION ERROR POPUP */}
      <ValidationErrorPopup
        isOpen={showValidationPopup}
        onClose={() => setShowValidationPopup(false)}
        errors={validationErrors}
      />

      <div className="call-container">
        <div className="call-header">
          <h1 className="heading">Book Appointment With Guru Ji</h1>
          <p className="refer-subtitle">Direct personal consultation with expert astrologers</p>
        </div>

        {/* Guru Profiles */}
        <section className="" ref={guruSectionRef}>
          <div className="section-header">
            <div className="">
              <h3 className="">Choose Your Spiritual Guide</h3>
            </div>
            <div className="scroll-controls">
              <button className="scroll-btn" onClick={scrollLeft} aria-label="Scroll left">‹</button>
              <button className="scroll-btn" onClick={scrollRight} aria-label="Scroll right">›</button>
            </div>
          </div>

          <div className="guru-profiles-container" ref={scrollRef}>
            {loading.gurus ? (
              <div className="loading-container">Finding your perfect guide...</div>
            ) : gurus.length > 0 ? (
              gurus.map(guru => (
                <GuruCard
                  key={guru.id}
                  guru={guru}
                  selected={selectedGuru === guru.id}
                  onSelect={() => {
                    setSelectedGuru(guru.id);
                    setSelectedTimeSlot(null);
                    setTimeout(() => scrollToSection(consultationSectionRef), 300);
                  }}
                  getAvatar={() => getGuruAvatar(guru.id)}
                />
              ))
            ) : (
              <div className="no-data">No spiritual guides available</div>
            )}
          </div>
        </section>

        {/* Consultation Types */}
        <section className="consultation-types scroll-section" ref={consultationSectionRef}>
          <h3>🔮 Choose Consultation Type</h3>
          <div className="types-grid">
            {loading.consultations ? (
              <div className="loading-container">Loading consultation types...</div>
            ) : consultationTypes.length > 0 ? (
              consultationTypes.map(type => (
                <ConsultationCard
                  key={type.id}
                  type={type}
                  selected={selectedConsultation === type.id}
                  onSelect={() => {
                    setSelectedConsultation(type.id);
                    setTimeout(() => scrollToSection(dateSectionRef), 300);
                  }}
                />
              ))
            ) : (
              <div className="no-data">No consultation types available</div>
            )}
          </div>
        </section>

        {/* Date selector */}
        <section className="consultation-types scroll-section" ref={dateSectionRef}>
          <div className="">
            <h3>📅 Select Date</h3>
            <button className="pick-date-btn" onClick={() => setShowCalendar(true)}>📆 Pick Date</button>
          </div>

          {customSelectedDate ? (
            <div className="custom-date-display">
              <div>Selected: {displayDate.fullDate}</div>
              <button onClick={() => { setCustomSelectedDate(null); setSelectedDate(0); setSelectedTimeSlot(null); }}>
                ← Back to Week View
              </button>
            </div>
          ) : (
            <div className="dates-grid">
              {weekDates.map((dateObj, idx) => (
                <button
                  key={dateObj.sqlDate}
                  className={`date-btn ${selectedDate === idx ? 'selected' : ''} ${dateObj.isToday ? 'today' : ''}`}
                  onClick={() => { setSelectedDate(idx); setCustomSelectedDate(null); setSelectedTimeSlot(null); setTimeout(() => scrollToSection(timeSectionRef), 300); }}
                >
                  <div className="">{dateObj.dayName}</div>
                  <div className="date-number">{dateObj.dayNumber}</div>
                  <div className="">{dateObj.monthName}</div>
                  {dateObj.isToday && <div className="today-indicator">Today</div>}
                </button>

              ))}

            </div>
          )}

          {showCalendar && (
            <CustomCalendar
              onDateSelect={(iso) => {
                const dateObj = new Date(iso);
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const diffDays = Math.floor((dateObj - today) / (1000 * 60 * 60 * 24));
                if (diffDays < 0 || diffDays > MAX_CUSTOM_DAYS) {
                  alert(`Please select a date within the next ${MAX_CUSTOM_DAYS} days`);
                  return;
                }
                setCustomSelectedDate(dateObj);
                setSelectedDate(null);
                setSelectedTimeSlot(null);
                if (selectedGuru) fetchAvailableSlots(selectedGuru, iso);
                setShowCalendar(false);
                setTimeout(() => scrollToSection(timeSectionRef), 500);
              }}
              minDate={getDateLimits().min}
              maxDate={getDateLimits().max}
              onClose={() => setShowCalendar(false)}
            />
          )}
        </section>

        {/* Time slots */}
        {selectedGuru && (
          <section className="consultation-types scroll-section" ref={timeSectionRef}>
            <h3>⏰ Available Time Slots - {displayDate.dayName}, {displayDate.monthName} {displayDate.dayNumber}</h3>
            <div className="slots-grid">
              {loading.slots ? (
                <div className="loading-container">Loading available slots...</div>
              ) : generateAvailableSlots().length > 0 ? (
                generateAvailableSlots().map(slot => (
                  <TimeSlotButton
                    key={slot.id}
                    slot={slot}
                    selected={selectedTimeSlot === slot.id}
                    onSelect={() => {
                      setSelectedTimeSlot(slot.id);
                      setTimeout(() => scrollToSection(bookingSectionRef), 300);
                    }}
                  />
                ))
              ) : (
                <div className="no-slots">No available slots for this day. Please select another date.</div>
              )}
            </div>
          </section>
        )}

        {/* Booking section */}

        <section className="consultation-types scroll-section" ref={bookingSectionRef}
          style={{
            display: "grid", justifyContent: "center", alignItems: "center"
          }}
        >
          <div className="booking-info"
          >
            <div
            >
              <h3>📋 Your Booking Details</h3>
              <div className="booking-details">
                <div className="detail-row"><span className="detail-label">Name:</span><span className="detail-value">{user?.full_name || user?.name || 'Please login'}</span></div>
                <div className="detail-row"><span className="detail-label">Current Credits:</span><span className="detail-value credit-highlight">💰 {user?.credits ?? 0}</span></div>
                <div className="detail-row"><span className="detail-label">Selected Guru:</span><span className="detail-value">{gurus.find(g => g.id === selectedGuru)?.name || ''}</span></div>
                <div className="detail-row"><span className="detail-label">Consultation:</span><span className="detail-value">{consultationTypes.find(c => c.id === selectedConsultation) ? `${consultationTypes.find(c => c.id === selectedConsultation).name} (${consultationTypes.find(c => c.id === selectedConsultation).duration_minutes} min)` : ''}</span></div>
                <div className="detail-row"><span className="detail-label">Date:</span><span className="detail-value">{displayDate.fullDate || `${displayDate.dayName}, ${displayDate.monthName} ${displayDate.dayNumber}`}</span></div>
                <div className="detail-row"><span className="detail-label">Time Slot:</span><span className="detail-value">{generateAvailableSlots().find(s => s.id === selectedTimeSlot)?.label || ''}</span></div>

                <div className="detail-row total-row">
                  <span className="detail-label">Credits Required:</span>
                  <span className="detail-value price-highlight">💳 {consultationTypes.find(c => c.id === selectedConsultation)?.credits_required ?? 0}</span>
                </div>

                {selectedConsultation && (
                  <div className="detail-row">
                    <span className="detail-label">Remaining After Booking:</span>
                    <span className="detail-value">{(user?.credits ?? 0) - (consultationTypes.find(c => c.id === selectedConsultation)?.credits_required ?? 0)} credits</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            className={`book-call-btn ${(!selectedTimeSlot || !selectedGuru || !selectedConsultation || !user || loading.booking) ? 'btn-disabled' : ''}`}
            onClick={bookCall}
            disabled={loading.booking}
          >
            {loading.booking ? '⏳ Booking...' : '📞 Book Call - Use Credits'}
          </button>
        </section>

        {/* Existing bookings */}
        {existingBookings.length > 0 && (
          <section className="existing-bookings scroll-section">
            <h3>📅 Your Bookings</h3>
            {loading.bookings ? (
              <div className="loading-container">Loading your bookings...</div>
            ) : (
              <div className="bookings-list">
                {existingBookings.slice(0, 5).map(b => (
                  <BookingItem
                    key={b.id}
                    booking={b}
                    getAvatar={() => getGuruAvatar(b.guru_id)}
                    onCancel={() => {
                      if (window.confirm(`Do you want to cancel booking with ${b.guru_name} on ${new Date(b.booking_date).toLocaleDateString()} at ${formatTime(b.start_hour, b.start_minute)}?`)) {
                        handleCancelBooking(b.id);
                      }
                    }}
                    loading={loading.booking}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Features & emergency chat */}
        <section className="consultation-types scroll-section">
          <h3>🌟 What You Get</h3>
          <div className="features-list">
            <div className="feature-item"><span className="feature-icon">🎯</span><span className="refer-subtitle nomargin">Personalized guidance based on your birth chart</span></div>
            <div className="feature-item"><span className="feature-icon">📞</span><span className="refer-subtitle nomargin">Direct phone consultation with expert</span></div>
            <div className="feature-item"><span className="feature-icon">📝</span><span className="refer-subtitle nomargin">Detailed remedies and suggestions</span></div>
            <div className="feature-item"><span className="feature-icon">🔄</span><span className="refer-subtitle nomargin">Follow-up support via chat</span></div>
          </div>
        </section>
      </div>
    </>
  );
}

export default CallGuru;
